import getSymbolFromCurrency from "currency-symbol-map";

import { CURRENCY_CONFIG } from "./config/config.Currencies";
const K_CF_SHOW_FUN_CURRENCY = false;

interface FormatOptions {
  code: string;
  symbol: string;
  before: boolean;
  decimalSeparator: string;
  thousandSeparator: string;
  decimalsNumber: number;
  coinsRatio: number;
}

export default class CurrencyFormat {
  public formatOptions: FormatOptions;

  constructor() {
    // default format options.
    // Should correspond to the format saved in gameReducer!
    this.formatOptions = {
      code: "",
      symbol: "",
      before: true,
      decimalSeparator: ".",
      thousandSeparator: ",",
      decimalsNumber: 2,
      coinsRatio: 1,
    };
  }

  // expects object in default format (see constructor)
  setFormatOptions(_formatOptions: any) {
    this.formatOptions = _formatOptions;
    //#CUSTOM: hide FUN currency or show euros
    if (K_CF_SHOW_FUN_CURRENCY === false) {
      if (
        this.formatOptions.symbol === "FUN" ||
        this.formatOptions.code === "FUN"
      ) {
        this.formatOptions = {
          ...this.formatOptions,
          symbol: "FUN",
          code: "FUN",
        };
      }
    }

    if (
      this.formatOptions.symbol.toString() !== "" &&
      this.formatOptions.symbol.toString() !== "null"
    ) {
      this.formatOptions = {
        ...this.formatOptions,
        symbol: this.convertUnicode(this.formatOptions.symbol),
      };
    } else {
      const currentSymbol = CURRENCY_CONFIG.find(
        (_el) => _el.code === this.formatOptions.code
      );

      this.formatOptions = {
        ...this.formatOptions,
        symbol: currentSymbol ? currentSymbol.symbol : this.formatOptions.code,
      };
    }
  }

  convertUnicode(input: any) {
    return input.replace(/\\u[0-9a-fA-F]{4}/g, function (a: any, b: any) {
      var charcode = parseInt(b, 16);
      return String.fromCharCode(charcode);
    });
  }

  toFixedNoRound(number: number, precision = 1) {
    const factor = Math.pow(10, precision);
    return Math.floor(number * factor) / factor;
  }

  // get number formatted to currency in string
  format(_number: number) {
    if (typeof _number === "undefined" || isNaN(_number)) return "NaN";
    const stringArray = _number
      .toFixed(this.formatOptions.decimalsNumber)
      .toString()
      .split(".") as any;
    const value = this.getNumberTSeparator(stringArray[0]);

    return this.getFormattedValDecimals(
      this.formatOptions.symbol,
      value,
      stringArray[1]
    );
  }

  formatFromCurrency(_number: any, _currency: string) {
    if (typeof _number === "undefined" || isNaN(_number)) return "NaN";
    const stringArray = parseFloat(_number)
      .toFixed(this.formatOptions.decimalsNumber)
      .toString()
      .split(".") as any;
    const value = this.getNumberTSeparator(stringArray[0]);

    return this.getFormattedValDecimals(
      getSymbolFromCurrency(_currency) as any,
      value,
      stringArray[1]
    );
  }

  // get number formatted to currency in string
  getFormattedNoDecimals(_number: number) {
    if (typeof _number === "undefined" || isNaN(_number)) return "NaN";
    const stringArray = _number
      .toFixed(this.formatOptions.decimalsNumber)
      .toString()
      .split(".") as any;
    const value = this.getNumberTSeparator(stringArray[0]);

    return this.getFormattedValDecimals(this.formatOptions.symbol, value);
  }

  // formats the currency but doesn't add the currency symbol
  getFormattedNoSymbol(_number: number) {
    if (typeof _number === "undefined" || isNaN(_number)) return "NaN";
    const stringArray = _number
      .toFixed(this.formatOptions.decimalsNumber)
      .toString()
      .split(".") as any;
    const value = this.getNumberTSeparator(stringArray[0]);

    return this.getFormattedValDecimals("", value, stringArray[1]);
  }

  // get number converted to coins in string
  getFormattedToCoins(_number: number) {
    if (typeof _number === "undefined" || isNaN(_number)) return "coinsNaN";
    // get value in coins
    let nval = _number * this.formatOptions.coinsRatio;
    const stringArray = nval
      .toFixed(this.formatOptions.decimalsNumber)
      .toString()
      .split(".") as any;
    const value = this.getNumberTSeparator(stringArray[0]);

    return this.getFormattedValDecimals("", value, stringArray[1]);
  }

  getFormattedValDecimals(
    _currCode: string,
    _value: number,
    _decimals?: any
  ): string {
    if (this.formatOptions.before) {
      if (_decimals) {
        return (
          _currCode +
          "" +
          _value +
          this.formatOptions.decimalSeparator +
          _decimals
        );
      } else {
        return _currCode + "" + _value;
      }
    } else {
      if (_decimals) {
        return (
          _value +
          this.formatOptions.decimalSeparator +
          _decimals +
          "" +
          _currCode
        );
      } else {
        return _value + "" + _currCode;
      }
    }
  }

  // get thousands separator
  getNumberTSeparator(_number: any) {
    let value = _number;

    for (let i = value.length - 1; i > 0; i--) {
      if ((i + 1) % 3 === 0 && value.length - i - 1 !== 0) {
        value = [
          value.slice(0, value.length - i - 1),
          this.formatOptions.thousandSeparator,
          value.slice(value.length - i - 1),
        ].join("");
      }
    }

    return value;
  }
}
